import { SUPPORTED_LANGUAGES } from "@/lib/constants";
import type { ApiFieldTranslations, ApiFieldTranslationsInput } from "@/types";

/** Structurally matches the `lang` shape every wizard `LanguageVariant` carries — kept local (not imported from the store) to avoid a utils → store → utils cycle. */
interface LangRef {
  code: string;
  name: string;
  flag: string;
}

export interface FieldVariant {
  lang: LangRef;
  value: string;
}

/** Looks up a lang code against the app's supported list for its flag/local name — falls back to the bare code for anything the API returns that isn't one of them. */
export function languageFromCode(code: string): LangRef {
  const known = SUPPORTED_LANGUAGES.find((l) => l.code === code);
  return known ? { code: known.code, name: known.localName, flag: known.flag } : { code, name: code, flag: "" };
}

/** Same extraction as `extractFieldVariants`, but into the plain `{langCode: value}` shape Amenities' staff/addon local state uses instead of the full `{lang, value}[]` variant list. */
export function extractPlainFieldMap(translations: ApiFieldTranslations | undefined, field: string): Record<string, string> {
  if (!translations) return {};
  const out: Record<string, string> = {};
  for (const [langCode, fields] of Object.entries(translations)) {
    const value = fields?.[field]?.value;
    if (value) out[langCode] = value;
  }
  return out;
}

/** `Record<langCode, text>` (the plain shape flat local state uses, e.g. a category's own `translations`) → the `{lang, value}[]` shape `mergeVariantsToWire` expects. */
export function toFieldVariants(record: Record<string, string> | undefined): FieldVariant[] {
  return Object.entries(record ?? {})
    .filter(([, v]) => v.trim())
    .map(([code, value]) => ({ lang: languageFromCode(code), value }));
}

/** Pulls one field's per-language values out of an API `translations` blob (keyed lang → field) into the flat variant list the UI works with. */
export function extractFieldVariants(translations: ApiFieldTranslations | undefined, field: string): FieldVariant[] {
  if (!translations) return [];
  const out: FieldVariant[] = [];
  for (const [langCode, fields] of Object.entries(translations)) {
    const value = fields?.[field]?.value;
    if (value) out.push({ lang: languageFromCode(langCode), value });
  }
  return out;
}

/**
 * Combines a term row's `label`+`description` into one variant per language that has
 * either set — the shape `translations.houseRules`/`paymentMethods`/`safetyDevices`/
 * `safetyConsiderations`/`accessibilityCommon` store locally (label and description
 * travel together per language, unlike the single-value fields `extractFieldVariants`
 * handles). Structurally matches the store's own `TermLanguageVariant`, kept local for
 * the same reason `LangRef` is (avoids a utils → store → utils import cycle).
 */
export function extractTermVariants(
  translations: ApiFieldTranslations | undefined,
): { lang: LangRef; label: string; description: string }[] {
  if (!translations) return [];
  const out: { lang: LangRef; label: string; description: string }[] = [];
  for (const [langCode, fields] of Object.entries(translations)) {
    const label = fields?.label?.value ?? "";
    const description = fields?.description?.value ?? "";
    if (label || description) out.push({ lang: languageFromCode(langCode), label, description });
  }
  return out;
}

/**
 * Same idea as `extractTermVariants`, for a cancellation slot's `name`+`badge`+
 * `summary` — confirmed live (round-trip test, 2026-09-28) that
 * `CancellationSlotDto.translations` is echoed back on both a preset
 * (`policyId`-referenced) and a custom slot, with `name`/`badge`/`summary`
 * each getting their own `stale` flag like every other translated field.
 */
export function extractCancellationSlotVariants(
  translations: ApiFieldTranslations | undefined,
): { lang: LangRef; name: string; badge: string; summary: string }[] {
  if (!translations) return [];
  const out: { lang: LangRef; name: string; badge: string; summary: string }[] = [];
  for (const [langCode, fields] of Object.entries(translations)) {
    const name = fields?.name?.value ?? "";
    const badge = fields?.badge?.value ?? "";
    const summary = fields?.summary?.value ?? "";
    if (name || badge || summary) out.push({ lang: languageFromCode(langCode), name, badge, summary });
  }
  return out;
}

/**
 * Merges several fields' variant lists into the one lang-keyed `{ lang: { field: value|null } }`
 * object the API's PATCH bodies expect. A language with no current variant for a field is sent
 * as `null` (not omitted) — the API does a partial merge keyed on whatever's present in the
 * request, so omitting a removed variant's language leaves the old value untouched server-side
 * instead of clearing it. `activeLangCodes` (the full selectable language list) ensures a
 * variant removed entirely from local state still gets an explicit null; falls back to only the
 * languages present in `fields` if that list isn't loaded yet.
 */
export function mergeVariantsToWire(
  fields: Record<string, FieldVariant[] | undefined>,
  activeLangCodes: string[],
): ApiFieldTranslationsInput {
  const langCodes = new Set(activeLangCodes);
  for (const variants of Object.values(fields)) {
    for (const v of variants ?? []) langCodes.add(v.lang.code);
  }

  const out: ApiFieldTranslationsInput = {};
  for (const [field, variants] of Object.entries(fields)) {
    for (const code of langCodes) {
      const value = variants?.find((v) => v.lang.code === code)?.value.trim() || null;
      out[code] = { ...out[code], [field]: value };
    }
  }
  return out;
}
